import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Button, 
  Container, 
  Typography, 
  Card, 
  Grid,
  Divider,
  LinearProgress,
  Paper,
  Skeleton,
  alpha,
  useTheme,
  Chip,
  TextField,
  InputAdornment
} from '@material-ui/core';
import { motion, AnimatePresence } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';
import ArrowLeftIcon from '../../icons/ArrowLeft';
import ChevronDownIcon from '../../icons/ChevronDown';
import StarIcon from '@material-ui/icons/Star';
import PeopleIcon from '@material-ui/icons/People';
import LayersIcon from '@material-ui/icons/Layers';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline';
import SearchIcon from '@material-ui/icons/Search';
import { questionnaireApi } from '../../api/questionnaireApi';
import useSettings from '../../hooks/useSettings';
import { Helmet } from 'react-helmet-async';

// 1. High-Tech Segmented Gauge (SCALED DOWN)
const HighTechGauge = ({ value, color, size = 56 }) => {
  const strokeWidth = 4;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - (value / 100) * circumference;

  return (
    <Box sx={{ position: 'relative', width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)', overflow: 'visible' }}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={alpha(color, 0.15)}
          strokeWidth={strokeWidth}
          strokeDasharray="3 3"
        />
        <motion.circle
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset }}
          transition={{ duration: 1.5, ease: "easeOut", delay: 0.2 }}
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="url(#gradient)"
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeLinecap="round"
          style={{ filter: `drop-shadow(0px 0px 6px ${alpha(color, 0.6)})` }}
        />
        <defs>
            <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor={color} />
                <stop offset="100%" stopColor="#FFFFFF" />
            </linearGradient>
        </defs>
      </svg>
      <Box sx={{ position: 'absolute', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 900, color: '#0F172A', lineHeight: 1, letterSpacing: '-0.02em' }}>
          {value}
        </Typography>
      </Box>
    </Box>
  );
};

// 2. Animated Mesh Background
const AnimatedMeshBackground = () => (
    <Box sx={{ 
        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, 
        overflow: 'hidden', zIndex: 0, opacity: 0.5,
        background: '#F8FAFC'
    }}>
        <motion.div
            animate={{ x: [0, 100, -50, 0], y: [0, -100, 50, 0], scale: [1, 1.2, 0.9, 1] }}
            transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
            style={{ position: 'absolute', top: '-10%', left: '-10%', width: '40vw', height: '40vw', background: 'radial-gradient(circle, rgba(99,102,241,0.1) 0%, rgba(255,255,255,0) 70%)', borderRadius: '50%', filter: 'blur(50px)' }}
        />
        <motion.div
            animate={{ x: [0, -80, 60, 0], y: [0, 80, -40, 0], scale: [1, 1.1, 0.8, 1] }}
            transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
            style={{ position: 'absolute', bottom: '-20%', right: '-10%', width: '50vw', height: '50vw', background: 'radial-gradient(circle, rgba(16,185,129,0.08) 0%, rgba(255,255,255,0) 70%)', borderRadius: '50%', filter: 'blur(50px)' }}
        />
    </Box>
);

const MotionCard = motion(Card);

const FormationGlassCard = ({ formation, index }) => {
  const [open, setOpen] = useState(false);
  
  const participationRate = formation.total_participants > 0 
    ? (formation.total_respondents / formation.total_participants) * 100 
    : 0;

  const getScoreTheme = (score) => {
    if (score >= 90) return { color: '#10B981', gradient: 'linear-gradient(135deg, #10B981 0%, #34D399 100%)', label: 'TRÈS BIEN', bg: '#D1FAE5' };
    if (score >= 75) return { color: '#6366F1', gradient: 'linear-gradient(135deg, #6366F1 0%, #818CF8 100%)', label: 'BIEN', bg: '#E0E7FF' };
    if (score >= 50) return { color: '#F59E0B', gradient: 'linear-gradient(135deg, #F59E0B 0%, #FCD34D 100%)', label: 'ASSEZ BIEN', bg: '#FEF3C7' };
    return { color: '#EF4444', gradient: 'linear-gradient(135deg, #EF4444 0%, #F87171 100%)', label: 'PASSABLE', bg: '#FEE2E2' };
  };

  const sTheme = getScoreTheme(formation.global_average);

  return (
    <MotionCard 
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, type: 'spring', stiffness: 90, damping: 20 }}
      sx={{ 
        mb: 2, 
        borderRadius: '16px', 
        background: 'rgba(255, 255, 255, 0.8)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255,255,255,0.9)',
        boxShadow: '0 4px 12px 0 rgba(31, 38, 135, 0.03)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0, left: 0, width: '4px', height: '100%',
            background: sTheme.gradient,
            borderTopLeftRadius: '16px',
            borderBottomLeftRadius: '16px'
        }
      }}
    >
      <Box sx={{ p: 2, position: 'relative', zIndex: 1 }}>
        <Grid container spacing={2} alignItems="center">
          
          <Grid item xs={12} md={1.5} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <HighTechGauge value={formation.global_average} color={sTheme.color} size={60} />
            <Box sx={{ mt: 0.5, px: 1, py: 0.25, borderRadius: '6px', bgcolor: sTheme.bg }}>
              <Typography variant="caption" sx={{ fontWeight: 800, color: sTheme.color, fontSize: '0.55rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {sTheme.label}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={5}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1, color: '#0F172A', letterSpacing: '-0.01em', lineHeight: 1.2 }}>
              {formation.name}
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.5, bgcolor: '#F8FAFC', borderRadius: '8px', border: '1px solid #E2E8F0' }}>
                    <LayersIcon sx={{ fontSize: 14, color: '#64748B' }} />
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569' }}>
                        <Box component="span" sx={{ color: '#0F172A', fontWeight: 800 }}>{formation.total_sessions}</Box> Sess.
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.5, bgcolor: '#F8FAFC', borderRadius: '8px', border: '1px solid #E2E8F0' }}>
                    <PeopleIcon sx={{ fontSize: 14, color: '#64748B' }} />
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569' }}>
                        <Box component="span" sx={{ color: '#0F172A', fontWeight: 800 }}>{formation.total_respondents} / {formation.total_participants}</Box> Part.
                    </Typography>
                </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box sx={{ bgcolor: 'rgba(255,255,255,0.7)', p: 1.5, borderRadius: '12px', border: '1px dashed #E2E8F0', position: 'relative' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748B', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <ChatBubbleOutlineIcon sx={{ fontSize: 14 }} /> Engagement
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 800, color: '#0F172A' }}>
                        {Math.round(participationRate)}%
                    </Typography>
                </Box>
                <LinearProgress 
                    variant="determinate" 
                    value={participationRate} 
                    sx={{ height: 6, borderRadius: 3, bgcolor: '#F1F5F9', '& .MuiLinearProgress-bar': { borderRadius: 3, background: sTheme.gradient } }}
                />
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#94A3B8', fontSize: '0.65rem' }}>
                    <strong style={{ color: '#475569' }}>{formation.total_respondents}</strong> sur {formation.total_participants} avis
                </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={1.5} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              size="small"
              onClick={() => setOpen(!open)}
              sx={{ 
                borderRadius: '8px',
                px: 2,
                background: open ? '#1E293B' : '#F1F5F9',
                color: open ? '#FFFFFF' : '#475569',
                fontWeight: 700,
                fontSize: '0.75rem',
                textTransform: 'none',
                '&:hover': { background: open ? '#0F172A' : '#E2E8F0' }
              }}
              endIcon={
                  <motion.div animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.3 }}>
                      <ChevronDownIcon style={{ fontSize: 16 }} />
                  </motion.div>
              }
            >
              {open ? 'Fermer' : 'Détails'}
            </Button>
          </Grid>
        </Grid>
      </Box>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Divider sx={{ borderColor: 'rgba(0,0,0,0.05)' }} />
            <Box sx={{ p: 2, background: 'rgba(248, 250, 252, 0.6)' }}>
              
              <Grid container spacing={1.5}>
                {formation.sessions.map((session, idx) => {
                  const sColor = session.average !== null ? getScoreTheme(session.average).color : '#94A3B8';
                  return (
                    <Grid item xs={12} sm={6} md={3} lg={2} key={session.id}>
                      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.03 }}>
                        <Paper 
                          sx={{ 
                            p: 1.5, 
                            borderRadius: '12px', 
                            background: '#FFFFFF',
                            border: `1px solid ${session.average !== null ? alpha(sColor, 0.2) : '#E2E8F0'}`,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
                            position: 'relative',
                            overflow: 'hidden'
                          }}
                        >
                          <Box sx={{ position: 'absolute', top: -15, right: -15, width: 40, height: 40, background: `radial-gradient(circle, ${alpha(sColor, 0.15)} 0%, rgba(255,255,255,0) 70%)`, borderRadius: '50%' }} />
                          
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 800, color: '#334155' }}>
                                S{session.name}
                            </Typography>
                            {session.average !== null ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, color: sColor }}>
                                    <StarIcon sx={{ fontSize: 12 }} />
                                    <Typography variant="caption" sx={{ fontWeight: 900 }}>{session.average}</Typography>
                                </Box>
                            ) : (
                                <Typography variant="caption" sx={{ color: '#94A3B8', fontSize: '0.6rem', fontStyle: 'italic' }}>En attente</Typography>
                            )}
                          </Box>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PeopleIcon sx={{ fontSize: 12, color: '#94A3B8' }} />
                            <Typography variant="caption" sx={{ color: '#64748B', fontSize: '0.65rem', fontWeight: 600 }}>
                                {session.respondents_count} / {session.participants_count}
                            </Typography>
                            <Box sx={{ flexGrow: 1 }} />
                            <LinearProgress 
                                variant="determinate" 
                                value={session.participants_count > 0 ? (session.respondents_count / session.participants_count) * 100 : 0} 
                                sx={{ width: 30, height: 3, borderRadius: 2, bgcolor: '#F1F5F9', '& .MuiLinearProgress-bar': { bgcolor: sColor } }}
                            />
                          </Box>
                        </Paper>
                      </motion.div>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </MotionCard>
  );
};

// Scaled Down Bento Box Header
const HeroBentoBox = ({ globalAvg, totalResp, loading }) => {
    return (
        <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={12} md={7}>
                <Paper sx={{ 
                    p: 2.5, borderRadius: '16px', 
                    background: 'rgba(255,255,255,0.8)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.9)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
                    height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center'
                }}>
                    <Typography variant="h5" sx={{ fontWeight: 900, color: '#0F172A', letterSpacing: '-0.02em', mb: 0.5 }}>
                        Excellence Qualité
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748B', fontWeight: 500 }}>
                        Pilotage de la satisfaction apprenants.
                    </Typography>
                </Paper>
            </Grid>

            <Grid item xs={12} md={5}>
                <Grid container spacing={2} sx={{ height: '100%' }}>
                    <Grid item xs={6}>
                        <Paper sx={{ 
                            p: 2, borderRadius: '16px', height: '100%',
                            background: 'linear-gradient(135deg, #1E293B 0%, #0F172A 100%)',
                            color: '#FFFFFF', position: 'relative', overflow: 'hidden',
                            boxShadow: '0 10px 15px -3px rgba(15, 23, 42, 0.2)',
                            display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center'
                        }}>
                            {loading ? <Skeleton variant="text" width={60} height={40} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} /> : (
                                <Typography variant="h3" sx={{ fontWeight: 900, lineHeight: 1 }}>{globalAvg}<span style={{ fontSize: '1.2rem', color: '#94A3B8' }}>%</span></Typography>
                            )}
                            <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 700, textTransform: 'uppercase', mt: 0.5 }}>Score Global</Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={6}>
                        <Paper sx={{ 
                            p: 2, borderRadius: '16px', height: '100%',
                            background: 'rgba(255,255,255,0.8)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.9)',
                            display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.03)'
                        }}>
                            {loading ? <Skeleton variant="text" width={60} height={40} /> : (
                                <Typography variant="h3" sx={{ fontWeight: 900, color: '#0F172A', lineHeight: 1 }}>{totalResp}</Typography>
                            )}
                            <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 700, textTransform: 'uppercase', mt: 0.5 }}>Avis Collectés</Typography>
                        </Paper>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
};

const LoadingSkeleton = () => (
    <Box sx={{ mt: 2 }}>
        {[1, 2, 3].map((i) => (
            <Card key={i} sx={{ mb: 2, borderRadius: '16px', p: 2, border: '1px solid #E2E8F0', boxShadow: 'none' }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={1.5} sx={{ display: 'flex', justifyContent: 'center' }}><Skeleton variant="circular" width={50} height={50} /></Grid>
                    <Grid item xs={5}>
                        <Skeleton variant="text" width="80%" height={24} />
                        <Skeleton variant="text" width="40%" height={16} />
                    </Grid>
                    <Grid item xs={4}><Skeleton variant="rectangular" height={36} sx={{ borderRadius: '8px' }} /></Grid>
                    <Grid item xs={1.5} sx={{ display: 'flex', justifyContent: 'flex-end' }}><Skeleton variant="rectangular" width={60} height={30} sx={{ borderRadius: '8px' }} /></Grid>
                </Grid>
            </Card>
        ))}
    </Box>
);

const VisualisationGlobale = () => {
  const { settings } = useSettings();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const accessToken = window.localStorage.getItem('accessToken');
      const response = await questionnaireApi.getGlobalFormationsSatisfactionSummary(accessToken);
      if (response.status) {
        setData(response.data);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const globalAvg = data.length > 0 ? Math.round(data.reduce((acc, curr) => acc + curr.global_average, 0) / data.length) : 0;
  const totalResp = data.reduce((acc, curr) => acc + curr.total_respondents, 0);

  const filteredData = data.filter(formation => 
    formation.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <>
      <Helmet>
        <title>Satisfaction Qualité | Apteed</title>
      </Helmet>
      <Box sx={{ 
          bgcolor: '#F8FAFC', 
          minHeight: '100%', 
          position: 'relative',
          overflow: 'hidden',
          pb: 8,
          pt: 4
      }}>
        
        <AnimatedMeshBackground />

        <Container maxWidth={settings.compact ? 'lg' : false} sx={{ position: 'relative', zIndex: 1 }}>
          
          <Box sx={{ mb: 2 }}>
            <Button
                component={RouterLink}
                to="/dashboard/questionnaire-satisfaction"
                startIcon={<ArrowLeftIcon style={{ fontSize: 14 }} />}
                size="small"
                sx={{ 
                    color: '#6366F1', 
                    fontWeight: 800, 
                    textTransform: 'none', 
                    bgcolor: 'rgba(99, 102, 241, 0.05)', 
                    borderRadius: '10px',
                    px: 2,
                    '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.1)' }
                }}
            >
                Retour au suivi
            </Button>
          </Box>

          <HeroBentoBox 
            globalAvg={globalAvg} 
            totalResp={totalResp} 
            loading={loading} 
          />

          {!loading && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
                <Box sx={{ mb: 4, maxWidth: { md: '400px', xs: '100%' } }}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Filtrer par formation..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        variant="outlined"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: '#6366F1', fontSize: 18 }} />
                                </InputAdornment>
                            ),
                            sx: { 
                                borderRadius: '16px', 
                                bgcolor: 'rgba(255, 255, 255, 0.8)',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(255,255,255,0.9)',
                                boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)',
                                '& fieldset': { border: 'none' },
                                '&:hover fieldset': { border: 'none' },
                            }
                        }}
                    />
                </Box>
            </motion.div>
          )}

          <Box>
            {loading ? (
                <LoadingSkeleton />
            ) : filteredData.length === 0 ? (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                <Paper sx={{ p: 6, textAlign: 'center', borderRadius: '20px', background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(10px)', border: '2px dashed #CBD5E1', boxShadow: 'none' }}>
                    <SearchIcon sx={{ color: '#94A3B8', fontSize: 32, mb: 1 }} />
                    <Typography variant="h6" sx={{ color: '#0F172A', fontWeight: 800 }}>Aucune formation trouvée</Typography>
                    <Typography variant="body2" sx={{ color: '#64748B' }}>Essayez un autre mot-clé pour votre recherche.</Typography>
                </Paper>
              </motion.div>
            ) : (
              <Box>
                {filteredData.map((formation, index) => (
                  <FormationGlassCard key={formation.id} formation={formation} index={index} />
                ))}
              </Box>
            )}
          </Box>


        </Container>
      </Box>
    </>
  );
};

export default VisualisationGlobale;
